import { useState } from 'react';
import * as http from '../../../../../utils/http';
import type { ConversationFileData } from '../../../utils/readFilesAsAttachmentData';

export interface ChatMessage {
	content: string;
	post_time: string;
	doctor_name: string | null;
	user_type: string;
	mentions: any[];
	attachments: any[];
	request_category: string | null;
}

/** The patient's three separate chat threads. Each is isolated end to end. */
export type ChatChannel = 'doctor' | 'nutritionist' | 'support';

interface UseDoctorChatParams {
	country: string;
	language: string;
	channel: ChatChannel;
	role?: string;
}

/**
 * Powers the Care+ chat popups by reusing the existing ask-a-doctor conversation
 * endpoints — GET to load the thread, and the same multipart POST the full
 * Ask-a-Doctor page uses to send a message.
 *
 * Both calls are scoped by `channel`, so the doctor, nutritionist and support
 * threads never show each other's history.
 */

export const useDoctorChat = ({ country, language, channel, role }: UseDoctorChatParams) => {
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [selectedIssue, setSelectedIssue] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [sending, setSending] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);

	const load = () => {
		setLoading(true);
		setError(false);

		http
			.getFromServer(`${country}/account/ask-a-doctor?channel=${channel}`, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response: any) => {
				const active: ChatMessage[] = response.data?.conversations?.active ?? [];
				// `historicals` is an array of per-conversation arrays (the full Ask-a-Doctor
				// page renders each group separately). The Care+ popup shows a single running
				// thread, so flatten it and merge with `active`: once staff replies, the server
				// buckets the whole conversation into `historicals`, and reading only `active`
				// made the thread look deleted here. The server now returns only this channel's
				// conversations, so flattening can no longer pull in another thread's history.
				const historicalGroups: any[] = response.data?.conversations?.historicals ?? [];
				const historical: ChatMessage[] = historicalGroups.flat();
				const options: any[] = response.data?.askADoctorOptions ?? [];

				// `post_time` is already a localized display string (e.g. "12 August 2026, 14:30"),
				// so it is not sortable/parsable here. Both buckets are emitted in the server's
				// own post order, so concatenating preserves it: historical posts precede the
				// active tail of the same thread.
				const thread: ChatMessage[] = [...historical, ...active];

				setMessages(thread);
				// Continue the existing conversation's category; otherwise fall back to
				// the first available request category so a first message can be sent.
				const latestCategory = [...thread].reverse().find((m) => m.request_category)?.request_category;
				setSelectedIssue(latestCategory ?? options[0]?.option_name ?? '');
			})
			.catch((err: any) => {
				window.clientLogger.error('Error in useDoctorChat.load: ', err);
				setError(true);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const send = (inputText: string, files: ConversationFileData[] = []): Promise<boolean> => {
		if (!inputText.trim() || sending) {
			return Promise.resolve(false);
		}

		setSending(true);
		setError(false);

		return http
			.postMultiPartsToServer(
				'account-update/conversations',
				{
					inputText,
					// Scoped to this channel's messages, so a first message to the coach is not
					// mistaken for a reply just because a doctor thread already exists.
					isFirstPost: messages.length === 0,
					country,
					language,
					channel,
					mentionedProducts: [],
					mentionedOrders: [],
					selectedIssue,
				},
				files,
				[http.MIDDLEWARES.CUSTOMER_ID]
			)
			.then(() => {
				// Re-read so the freshly persisted message (and any doctor reply) shows.
				load();
				return true;
			})
			.catch((err: any) => {
				window.clientLogger.error('Error in useDoctorChat.send: ', err);
				setError(true);
				return false;
			})
			.finally(() => {
				setSending(false);
			});
	};

	return { messages, loading, sending, error, load, send };
};
